import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Notification types mapped to push settings keys
const CAMPAIGN_TYPES = new Set([
  'application', 'new_application', 'application_status', 'application_submitted',
  'content_submission', 'content_submitted', 'content_submitted_brand',
  'content_approval', 'revision_requested',
  'draft_submitted', 'draft_submission', 'draft_approved', 'draft_rejected',
  'proof_submission',
  'content_deadline_7days', 'content_deadline_3days', 'content_deadline_1day',
  'draft_submission_reminder', 'draft_approval_reminder', 'draft_resubmission_reminder'
]);
const PAYMENT_TYPES = new Set(['payment', 'payment_received', 'payment_processing']);
const MESSAGE_TYPES = new Set(['message']);

const getPushSettingKey = (type) => {
  if (CAMPAIGN_TYPES.has(type)) return 'pushCampaignUpdates';
  if (PAYMENT_TYPES.has(type)) return 'pushPayments';
  if (MESSAGE_TYPES.has(type)) return 'pushMessages';
  return null;
};

/**
 * Check if the recipient user has push/in-app notifications enabled for this type.
 * Defaults to enabled if settings are not set.
 */
const isPushAllowed = async (userId, type) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return true; // Default: allow if user doc doesn't exist
    const settings = userDoc.data().settings?.notifications || {};
    const key = getPushSettingKey(type);
    if (!key) return true; // Default: allow for unmapped types
    return settings[key] !== false; // Default: enabled unless explicitly false
  } catch {
    return true; // Default: allow on error (don't block notifications due to a settings fetch failure)
  }
};

/**
 * Notification Service - Handles all operations related to notifications
 */
const notificationService = {
  /**
   * Create a notification
   * Checks the recipient's push notification settings before creating.
   * @param {string} userId - User ID to receive the notification
   * @param {string} type - Notification type
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {object} relatedData - Related data (IDs and types)
   * @returns {Promise<object|null>} - Created notification object with ID, or null if skipped
   */
  createNotification: async (userId, type, title, message, relatedData = {}) => {
    try {
      // Check if the recipient has this notification type enabled
      const allowed = await isPushAllowed(userId, type);
      if (!allowed) {
        return null; // Recipient has disabled this notification type
      }

      const notificationsRef = collection(db, 'notifications');

      const notificationData = {
        userId,
        type,
        title,
        message,
        createdAt: serverTimestamp(),
        isRead: false,
        readAt: null,
        ...relatedData
      };

      const docRef = await addDoc(notificationsRef, notificationData);

      return { id: docRef.id, ...notificationData };
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  /**
   * Get notifications for a user
   * @param {string} userId - User ID
   * @param {object} options - Query options (limit, unreadOnly, type)
   * @returns {Promise<Array>} - Array of notification objects
   */
  getUserNotifications: async (userId, options = {}) => {
    try {
      const notificationsRef = collection(db, 'notifications');
      
      // Build query
      let q = query(
        notificationsRef, 
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      // Filter by read status if specified
      if (options.unreadOnly) {
        q = query(q, where('isRead', '==', false));
      }
      
      // Filter by type if specified
      if (options.type) {
        q = query(q, where('type', '==', options.type));
      }
      
      // Add limit if provided
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const querySnapshot = await getDocs(q);
      
      const notifications = [];
      querySnapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
      });
      
      return notifications;
    } catch (error) {
      console.error('Error getting user notifications:', error);
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - Promise that resolves when notification is marked as read
   */
  markAsRead: async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read for a user
   * @param {string} userId - User ID
   * @returns {Promise<number>} - Number of notifications marked as read
   */
  markAllAsRead: async (userId) => {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef, 
        where('userId', '==', userId),
        where('isRead', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      
      const updatePromises = [];
      querySnapshot.forEach((doc) => {
        updatePromises.push(
          updateDoc(doc.ref, {
            isRead: true,
            readAt: serverTimestamp()
          })
        );
      });
      
      await Promise.all(updatePromises);
      
      return querySnapshot.size; // Return number of notifications marked as read
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - Promise that resolves when notification is deleted
   */
  deleteNotification: async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  /**
   * Clear all notifications for a user
   * @param {string} userId - User ID
   * @returns {Promise<number>} - Number of notifications deleted
   */
  clearAllNotifications: async (userId) => {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(notificationsRef, where('userId', '==', userId));
      
      const querySnapshot = await getDocs(q);
      
      const deletePromises = [];
      querySnapshot.forEach((doc) => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      await Promise.all(deletePromises);
      
      return querySnapshot.size; // Return number of notifications deleted
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notification count for a user
   * @param {string} userId - User ID
   * @returns {Promise<number>} - Number of unread notifications
   */
  getUnreadCount: async (userId) => {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef, 
        where('userId', '==', userId),
        where('isRead', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting unread notification count:', error);
      throw error;
    }
  },

  /**
   * Create campaign application notification
   * @param {string} brandId - Brand user ID
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} influencerName - Influencer name
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createApplicationNotification: async (brandId, influencerId, campaignId, campaignTitle, influencerName) => {
    await notificationService.createNotification(
      brandId,
      'application',
      'New Campaign Application',
      `${influencerName} has applied to your campaign: ${campaignTitle}`,
      {
        relatedId: campaignId,
        relatedType: 'campaign',
        influencerId
      }
    );
  },

  /**
   * Create application status notification
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} status - Application status ('approved' or 'rejected')
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createApplicationStatusNotification: async (influencerId, campaignId, campaignTitle, status) => {
    const title = status === 'approved' ? 'Application Approved' : 'Application Not Selected';
    const message = status === 'approved' 
      ? `Your application for "${campaignTitle}" has been approved! You can now submit content.`
      : `Your application for "${campaignTitle}" was not selected at this time.`;
    
    await notificationService.createNotification(
      influencerId,
      'application_status',
      title,
      message,
      {
        relatedId: campaignId,
        relatedType: 'campaign',
        status
      }
    );
  },

  /**
   * Create content submission notification
   * @param {string} brandId - Brand user ID
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} influencerName - Influencer name
   * @param {string} submissionId - Submission ID
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createContentSubmissionNotification: async (
    brandId, 
    influencerId, 
    campaignId, 
    campaignTitle, 
    influencerName,
    submissionId
  ) => {
    await notificationService.createNotification(
      brandId,
      'content_submission',
      'New Content Submission',
      `${influencerName} has submitted content for your campaign: ${campaignTitle}`,
      {
        relatedId: submissionId,
        relatedType: 'submission',
        campaignId,
        influencerId
      }
    );
  },

  /**
   * Create content approval notification
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} submissionId - Submission ID
   * @param {string} status - Approval status ('approved', 'rejected', 'revision_requested')
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createContentApprovalNotification: async (
    influencerId, 
    campaignId, 
    campaignTitle, 
    submissionId,
    status
  ) => {
    let title, message;
    
    if (status === 'approved') {
      title = 'Content Approved';
      message = `Your content for "${campaignTitle}" has been approved!`;
    } else if (status === 'rejected') {
      title = 'Content Needs Revision';
      message = `Your content for "${campaignTitle}" needs revision. Please check feedback and resubmit.`;
    } else {
      title = 'Revision Requested';
      message = `Revisions requested for your content for "${campaignTitle}". Please check feedback.`;
    }
    
    await notificationService.createNotification(
      influencerId,
      'content_approval',
      title,
      message,
      {
        relatedId: submissionId,
        relatedType: 'submission',
        campaignId,
        status
      }
    );
  },

  /**
   * Create payment notification
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} paymentId - Payment ID
   * @param {number} amount - Payment amount
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createPaymentNotification: async (
    influencerId, 
    campaignId, 
    campaignTitle, 
    paymentId,
    amount
  ) => {
    await notificationService.createNotification(
      influencerId,
      'payment',
      'Payment Processed',
      `Your payment of $${amount} for "${campaignTitle}" has been processed.`,
      {
        relatedId: paymentId,
        relatedType: 'payment',
        campaignId
      }
    );
  },

  /**
   * Create message notification
   * @param {string} receiverId - Receiver user ID
   * @param {string} senderId - Sender user ID
   * @param {string} senderName - Sender name
   * @param {string} messagePreview - Preview of the message
   * @param {string} conversationId - Conversation ID
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createMessageNotification: async (
    receiverId, 
    senderId, 
    senderName, 
    messagePreview,
    conversationId
  ) => {
    await notificationService.createNotification(
      receiverId,
      'message',
      'New Message',
      `${senderName}: ${messagePreview}`,
      {
        relatedId: conversationId,
        relatedType: 'conversation',
        senderId
      }
    );
  },

  /**
   * Create content draft submission notification for brand
   * @param {string} brandId - Brand user ID
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} influencerName - Influencer name
   * @param {string} draftId - Draft ID
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createDraftSubmissionNotification: async (
    brandId,
    influencerId,
    campaignId,
    campaignTitle,
    influencerName,
    draftId
  ) => {
    try {
      return await notificationService.createNotification(
        brandId,
        'draft_submitted',
        'New Content Draft Submitted',
        `${influencerName} has submitted a content draft for "${campaignTitle}" that needs your review.`,
        {
          relatedId: draftId,
          relatedType: 'contentDraft',
          campaignId,
          influencerId
        }
      );
    } catch (error) {
      console.error('Error creating draft submission notification:', error);
      throw error;
    }
  },

  /**
   * Create content draft approval notification for influencer
   * @param {string} influencerId - Influencer user ID
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} draftId - Draft ID
   * @param {string} status - Draft status ('approved' or 'rejected')
   * @param {string} feedback - Optional feedback from brand
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createDraftApprovalNotification: async (
    influencerId,
    campaignId,
    campaignTitle,
    draftId,
    status,
    feedback = ''
  ) => {
    try {
      let title, message;
      
      if (status === 'approved') {
        title = 'Content Draft Approved!';
        message = `Your content draft for "${campaignTitle}" has been approved. You can now post your content and submit final proof.`;
      } else {
        title = 'Content Draft Needs Revision';
        message = `Your content draft for "${campaignTitle}" needs revision. Please check the feedback and resubmit.`;
      }
      
      return await notificationService.createNotification(
        influencerId,
        status === 'approved' ? 'draft_approved' : 'draft_rejected',
        title,
        message,
        {
          relatedId: draftId,
          relatedType: 'contentDraft',
          campaignId,
          feedback
        }
      );
    } catch (error) {
      console.error('Error creating draft approval notification:', error);
      throw error;
    }
  },

  /**
   * Create payment processing notification for admin
   * @param {string} adminId - Admin user ID
   * @param {string} brandId - Brand user ID
   * @param {string} brandName - Brand name
   * @param {string} influencerId - Influencer user ID
   * @param {string} influencerName - Influencer name
   * @param {string} campaignId - Campaign ID
   * @param {string} campaignTitle - Campaign title
   * @param {string} paymentId - Payment ID
   * @param {number} amount - Payment amount
   * @returns {Promise} - Promise that resolves when notification is created
   */
  createPaymentProcessingNotification: async (
    adminId,
    brandId,
    brandName,
    influencerId,
    influencerName,
    campaignId,
    campaignTitle,
    paymentId,
    amount
  ) => {
    try {
      return await notificationService.createNotification(
        adminId,
        'payment_processing',
        'Payment Processing Required',
        `Payment of ₱${amount.toLocaleString()} for ${brandName} → ${influencerName} campaign "${campaignTitle}" needs processing.`,
        {
          relatedId: paymentId,
          relatedType: 'payment',
          campaignId,
          brandId,
          influencerId,
          amount
        }
      );
    } catch (error) {
      console.error('Error creating payment processing notification:', error);
      throw error;
    }
  }
};

export default notificationService;
